import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Tooltip,
  Fab,
  Zoom,
  Skeleton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import TimerIcon from '@mui/icons-material/Timer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalculateIcon from '@mui/icons-material/Calculate';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import DnsIcon from '@mui/icons-material/Dns';
import BugReportIcon from '@mui/icons-material/BugReport';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventIcon from '@mui/icons-material/Event';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import ForumIcon from '@mui/icons-material/Forum';
import NotificationCenter from './NotificationCenter';
import CalculadorasMedicas from './CalculadorasMedicas';
import CentroAyuda from './CentroAyuda';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorUserMenu, setAnchorUserMenu] = useState(null);
  const [firebasePhotoURL, setFirebasePhotoURL] = useState("");

  // Estados para el perfil del usuario
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [userAgenda, setUserAgenda] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Estados para edición de agenda
  const [editingAgenda, setEditingAgenda] = useState(false);
  const [editAgendaDias, setEditAgendaDias] = useState([]);
  const [editAgendaHoraInicio, setEditAgendaHoraInicio] = useState("08:00");
  const [editAgendaHoraFin, setEditAgendaHoraFin] = useState("18:00");
  const [editAgendaIntervalo, setEditAgendaIntervalo] = useState(30);
  const [savingAgenda, setSavingAgenda] = useState(false);

  // Estado para calculadoras médicas
  const [openCalculadoras, setOpenCalculadoras] = useState(false);

  // Estado para centro de ayuda
  const [openAyuda, setOpenAyuda] = useState(false);

  // WhatsApp Dialog states
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState(false);
  const [waNumeroDestino, setWaNumeroDestino] = useState('');
  const [waMensaje, setWaMensaje] = useState('');
  const [waEnviando, setWaEnviando] = useState(false);
  const [waError, setWaError] = useState(null);
  const [waSuccess, setWaSuccess] = useState(null);

  // Estado para FAB
  const [fabOpen, setFabOpen] = useState(false);

  // Estado para métricas y próximas citas
  const [metrics, setMetrics] = useState({
    citasHoy: 0,
    citasPendientes: 0,
    mensajesNoLeidos: 0,
    pacientesNuevos: 0
  });
  const [proximasCitas, setProximasCitas] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  // Estado para dark mode (modo claro por defecto)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('dashboard_dark_mode');
    if (saved !== null) return JSON.parse(saved);
    return false;
  });

  // Guardar preferencia de dark mode
  useEffect(() => {
    localStorage.setItem('dashboard_dark_mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  // Saludo dinámico basado en la hora local del usuario
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'Buenos días';
    if (hour >= 12 && hour < 19) return 'Buenas tardes';
    return 'Buenas noches';
  };

  // Fecha actual formateada
  const getFormattedDate = () => {
    const now = new Date();
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    const formatted = now.toLocaleDateString('es-ES', options);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  // Cargar métricas reales y próximas citas
  useEffect(() => {
    const loadMetrics = async () => {
      setLoadingMetrics(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        // Cargar citas de hoy
        const citasRes = await fetch('/api/emr/citas/hoy', { headers });
        if (citasRes.ok) {
          const citasData = await citasRes.json();
          const ahora = new Date();

          // Filtrar citas futuras (que aún no han pasado)
          const citasFuturas = (citasData.citas || []).filter(cita => {
            const fechaCita = new Date(cita.fecha_cita);
            return fechaCita > ahora && cita.estado !== 'cancelado';
          });

          // Contar pendientes (sin estado o estado pendiente)
          const pendientes = (citasData.citas || []).filter(c =>
            !c.estado || c.estado === 'pendiente'
          ).length;

          setProximasCitas(citasFuturas.slice(0, 4)); // Próximas 4 para el resumen
          setMetrics(prev => ({
            ...prev,
            citasHoy: citasData.total || 0,
            citasPendientes: pendientes
          }));
        }

        // Cargar estadísticas adicionales
        const statsRes = await fetch('/api/emr/citas/estadisticas', { headers });
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setMetrics(prev => ({
            ...prev,
            citasHoy: stats.total_hoy || prev.citasHoy,
            citasPendientes: stats.pendientes || prev.citasPendientes
          }));
        }
      } catch (error) {
        console.error('Error cargando métricas:', error);
        // Mantener valores por defecto en caso de error
      } finally {
        setLoadingMetrics(false);
      }
    };
    loadMetrics();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u);
        if (u.photoURL) {
          setFirebasePhotoURL(u.photoURL);
        }
        try {
          const res = await fetch(`/api/users/by_uid/${u.uid}`);
          const data = await res.json();
          setIsSuperAdmin(data?.role === 'superadmin');
        } catch (e) {
          console.error("Error cargando perfil:", e);
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user_id");
      navigate('/login');
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const handleAvatarClick = (event) => setAnchorUserMenu(event.currentTarget);
  const handleUserMenuClose = () => setAnchorUserMenu(null);

  const fetchUserProfile = async () => {
    let data;
    if (user?.uid) {
      const res = await fetch(`/api/users/by_uid/${user.uid}`);
      data = await res.json();
    } else if (user?.id) {
      const res = await fetch(`/api/users/${user.id}`);
      data = await res.json();
    }
    setUserPlan(data?.empresa?.plan?.name || "Desconocido");
    setSubscriptionInfo({
      empresa: data?.empresa || {},
      plan: data?.empresa?.plan || {},
    });
    setUserAgenda(data?.agenda || null);
    setIsSuperAdmin(data?.role === 'superadmin');
  };

  const DIAS_SEMANA = [
    { id: "lunes", label: "Lun" },
    { id: "martes", label: "Mar" },
    { id: "miercoles", label: "Mié" },
    { id: "jueves", label: "Jue" },
    { id: "viernes", label: "Vie" },
    { id: "sabado", label: "Sáb" },
    { id: "domingo", label: "Dom" }
  ];

  const INTERVALOS = [
    { value: 15, label: "15 min" },
    { value: 20, label: "20 min" },
    { value: 30, label: "30 min" },
    { value: 45, label: "45 min" },
    { value: 60, label: "1 hora" }
  ];

  const startEditingAgenda = () => {
    const diasActuales = userAgenda?.dias ? userAgenda.dias.split(",").map(d => d.trim().toLowerCase()) : [];
    setEditAgendaDias(diasActuales);
    setEditAgendaHoraInicio(userAgenda?.hora_inicio || "08:00");
    setEditAgendaHoraFin(userAgenda?.hora_fin || "18:00");
    setEditAgendaIntervalo(userAgenda?.intervalo || 30);
    setEditingAgenda(true);
  };

  const cancelEditingAgenda = () => setEditingAgenda(false);

  const toggleDia = (diaId) => {
    setEditAgendaDias(prev =>
      prev.includes(diaId)
        ? prev.filter(d => d !== diaId)
        : [...prev, diaId]
    );
  };

  const saveAgenda = async () => {
    setSavingAgenda(true);
    try {
      let userId = user?.id;
      if (!userId && user?.uid) {
        const res = await fetch(`/api/users/by_uid/${user.uid}`);
        const data = await res.json();
        userId = data?.id;
      }
      if (!userId) {
        alert("No se pudo identificar el usuario");
        return;
      }

      const diasOrdenados = DIAS_SEMANA
        .filter(d => editAgendaDias.includes(d.id))
        .map(d => d.id);

      const response = await fetch(`/api/users/${userId}/agenda`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dias: diasOrdenados.join(", "),
          hora_inicio: editAgendaHoraInicio,
          hora_fin: editAgendaHoraFin,
          intervalo: editAgendaIntervalo
        })
      });

      if (response.ok) {
        const updatedAgenda = await response.json();
        setUserAgenda(updatedAgenda);
        setEditingAgenda(false);
      } else {
        alert("Error al guardar el horario");
      }
    } catch (err) {
      console.error("Error guardando agenda:", err);
      alert("Error al guardar el horario");
    } finally {
      setSavingAgenda(false);
    }
  };

  const handleOpenProfile = async () => {
    handleUserMenuClose();
    await fetchUserProfile();
    setOpenProfileDialog(true);
  };

  // ============================================
  // DEFINICIÓN DE APLICACIONES POR CATEGORÍAS
  // ============================================

  // Acciones Principales (Hero Cards con métricas)
  const heroApps = [
    {
      id: 'agenda',
      name: 'Agenda de Citas',
      description: 'Gestiona tus citas del día',
      icon: <CalendarMonthIcon />,
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      shadowColor: 'rgba(14, 165, 233, 0.4)',
      path: '/agenda',
      available: true,
      proximasCitas: proximasCitas,
      totalCitas: metrics.citasHoy
    },
    {
      id: 'emr',
      name: 'Expediente Médico',
      description: 'Historias clínicas con IA',
      icon: <LocalHospitalIcon />,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      shadowColor: 'rgba(16, 185, 129, 0.4)',
      path: '/emr',
      available: true,
      metric: loadingMetrics ? null : metrics.pacientesNuevos,
      metricLabel: 'pacientes nuevos',
      metricSub: 'este mes'
    },
    {
      id: 'analytics',
      name: 'Analíticas',
      description: 'Métricas y reportes',
      icon: <AnalyticsIcon />,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
      shadowColor: 'rgba(139, 92, 246, 0.4)',
      path: '/analytics',
      available: true,
      metricIcon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      metricLabel: 'Ver reportes',
      metricSub: null
    },
    ...(isSuperAdmin ? [{
      id: 'admin-empresas',
      name: 'Empresas',
      description: 'Ver todas las empresas',
      icon: <BusinessIcon />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      shadowColor: 'rgba(245, 158, 11, 0.4)',
      path: '/admin/empresas',
      available: true,
      metricIcon: <BusinessIcon sx={{ fontSize: 20 }} />,
      metricLabel: 'Gestionar',
      metricSub: null
    }] : [])
  ];

  // Herramientas
  const toolApps = [
    {
      id: 'constructor',
      name: 'Constructor de Flujos',
      description: 'Diseña flujos conversacionales',
      icon: <AccountTreeIcon />,
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      shadowColor: 'rgba(99, 102, 241, 0.4)',
      path: '/constructor',
      available: true
    },
    {
      id: 'smart-flows',
      name: 'Flujos Inteligentes',
      description: 'Chatbots 100% IA',
      icon: <AutoAwesomeIcon />,
      gradient: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
      shadowColor: 'rgba(168, 85, 247, 0.4)',
      path: '/smart-flows',
      available: true,
      badge: 'Beta'
    },
    {
      id: 'calculadoras',
      name: 'Calculadoras',
      description: 'IMC, Glasgow, Wells...',
      icon: <CalculateIcon />,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      shadowColor: 'rgba(249, 115, 22, 0.4)',
      path: null,
      available: true,
      isDialog: true,
      onOpen: () => setOpenCalculadoras(true)
    },
    {
      id: 'conversaciones',
      name: 'Conversaciones',
      description: 'Historial de chats',
      icon: <ForumIcon />,
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      shadowColor: 'rgba(14, 165, 233, 0.4)',
      path: '/conversaciones',
      available: true
    },
    {
      id: 'suscripcion',
      name: 'Mi Suscripción',
      description: 'Plan y facturación',
      icon: <CardMembershipIcon />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      shadowColor: 'rgba(245, 158, 11, 0.4)',
      path: '/suscripcion',
      available: true
    },
    {
      id: 'ayuda',
      name: 'Centro de Ayuda',
      description: 'Documentación',
      icon: <MenuBookIcon />,
      gradient: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
      shadowColor: 'rgba(8, 145, 178, 0.4)',
      path: null,
      available: true,
      isDialog: true,
      onOpen: () => setOpenAyuda(true)
    }
  ];

  // Herramientas Administrativas (Solo Superadmins)
  const adminApps = [
    {
      id: 'consumo-tokens',
      name: 'Consumo Servicios',
      description: 'IA, mensajería y embeddings',
      icon: <DataUsageIcon />,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      shadowColor: 'rgba(6, 182, 212, 0.4)',
      path: '/consumo-tokens',
      available: true
    },
    {
      id: 'admin-planes',
      name: 'Admin Planes',
      description: 'Gestión de planes',
      icon: <AdminPanelSettingsIcon />,
      gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
      shadowColor: 'rgba(220, 38, 38, 0.4)',
      path: '/admin/planes',
      available: true
    },
    {
      id: 'admin-services',
      name: 'Servicios',
      description: 'Monitor PM2',
      icon: <DnsIcon />,
      gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      shadowColor: 'rgba(5, 150, 105, 0.4)',
      path: '/admin/servicios',
      available: true
    },
    {
      id: 'admin-politicas',
      name: 'Políticas Privacidad',
      description: 'Control de versiones',
      icon: <GavelIcon />,
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
      shadowColor: 'rgba(124, 58, 237, 0.4)',
      path: '/admin/politicas',
      available: true
    },
    {
      id: 'admin-tests',
      name: 'Test Suite',
      description: 'Pruebas de chatbot',
      icon: <BugReportIcon />,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      shadowColor: 'rgba(249, 115, 22, 0.4)',
      path: '/admin/tests',
      available: true
    },
    {
      id: 'admin-corrections',
      name: 'Correcciones IA',
      description: 'Aprendizaje de errores',
      icon: <PsychologyIcon />,
      gradient: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
      shadowColor: 'rgba(225, 29, 72, 0.4)',
      path: '/admin/corrections',
      available: true
    },
    {
      id: 'admin-db',
      name: 'Base de Datos',
      description: 'Admin de BD',
      icon: <StorageIcon />,
      gradient: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
      shadowColor: 'rgba(71, 85, 105, 0.4)',
      path: '/admin/db',
      available: true
    },
    {
      id: 'design-studio',
      name: 'Design Studio',
      description: 'Diseña y propón cambios',
      icon: <CodeIcon />,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
      shadowColor: 'rgba(139, 92, 246, 0.4)',
      path: '/admin/design-studio',
      available: true,
      badge: 'NARA'
    },
  ];

  const handleAppClick = (app) => {
    if (app.available) {
      if (app.isDialog && app.onOpen) {
        app.onOpen();
      } else if (app.path) {
        navigate(app.path);
      }
    }
  };

  // Función para enviar WhatsApp
  const handleEnviarWhatsApp = async () => {
    if (!waNumeroDestino || !waMensaje) return;

    setWaEnviando(true);
    setWaError(null);
    try {
      const res = await fetch('/api/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: waNumeroDestino.startsWith('whatsapp:') ? waNumeroDestino : `whatsapp:${waNumeroDestino}`,
          body: waMensaje
        })
      });

      if (res.ok) {
        setWaSuccess('Mensaje enviado correctamente');
        setOpenWhatsAppDialog(false);
        setWaNumeroDestino('');
        setWaMensaje('');
        setTimeout(() => setWaSuccess(null), 3000);
      } else {
        const errorData = await res.json();
        setWaError(errorData.detail || 'Error al enviar mensaje');
      }
    } catch (err) {
      setWaError('Error de conexión');
    } finally {
      setWaEnviando(false);
    }
  };

  // Acciones rápidas del FAB
  const fabActions = [
    { icon: <EventIcon />, name: 'Nueva Cita', action: () => navigate('/agenda?new=true'), color: '#0ea5e9' },
    { icon: <PersonAddIcon />, name: 'Nuevo Paciente', action: () => navigate('/emr?new=true'), color: '#10b981' },
    { icon: <WhatsAppIcon />, name: 'Enviar WhatsApp', action: () => setOpenWhatsAppDialog(true), color: '#25D366' },
    { icon: <PlayArrowIcon />, name: 'Iniciar Consulta', action: () => navigate('/emr'), color: '#8b5cf6' }
  ];

  // ============================================
  // ESTILOS DE TARJETAS
  // ============================================

  // Estilo base para tarjetas con glassmorphism
  const getCardStyle = (app, isHero = false) => ({
    display: 'flex',
    flexDirection: isHero ? 'row' : 'column',
    alignItems: isHero ? 'flex-start' : 'center',
    gap: isHero ? 1.5 : 1,
    p: isHero ? { xs: 1.5, sm: 2 } : { xs: 1.25, sm: 1.5 },
    borderRadius: 3,
    background: darkMode
      ? 'rgba(30, 41, 59, 0.8)'
      : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    cursor: app.available ? 'pointer' : 'default',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: app.available ? 1 : 0.5,
    position: 'relative',
    boxShadow: darkMode
      ? '0 4px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 4px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
    border: darkMode
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(255, 255, 255, 0.5)',
    overflow: 'hidden',
    outline: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: app.gradient,
      opacity: app.available ? 1 : 0.3
    },
    '&:hover': app.available ? {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: darkMode
        ? `0 20px 40px rgba(0, 0, 0, 0.4), 0 8px 16px ${app.shadowColor}`
        : `0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px ${app.shadowColor}`
    } : {},
    '&:active': app.available ? {
      transform: 'translateY(-2px) scale(0.98)'
    } : {},
    '&:focus-visible': app.available ? {
      outline: `3px solid ${app.shadowColor}`,
      outlineOffset: 2
    } : {}
  });

  // Componente de tarjeta Hero
  const HeroCard = ({ app }) => (
    <Box
      onClick={() => handleAppClick(app)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAppClick(app); }}
      role="button"
      tabIndex={app.available ? 0 : -1}
      aria-label={`${app.name}. ${app.description}`}
      sx={getCardStyle(app, true)}
    >
      {/* Icon */}
      <Box
        sx={{
          width: { xs: 40, sm: 44 },
          height: { xs: 40, sm: 44 },
          borderRadius: 2,
          background: app.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          boxShadow: `0 4px 16px ${app.shadowColor}`,
          flexShrink: 0,
          '& svg': { fontSize: { xs: 20, sm: 22 } }
        }}
      >
        {app.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: darkMode ? '#f1f5f9' : '#1e293b',
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 700,
            lineHeight: 1.2
          }}
        >
          {app.name}
        </Typography>
        <Typography
          sx={{
            color: darkMode ? '#94a3b8' : '#64748b',
            fontSize: 11,
            mb: 0.75,
            display: { xs: 'none', sm: 'block' }
          }}
        >
          {app.description}
        </Typography>

        {/* Metric o Próximas Citas */}
        {loadingMetrics ? (
          <Skeleton variant="text" width={40} height={24} />
        ) : app.proximasCitas !== undefined ? (
          // Mostrar próximas citas para la tarjeta de Agenda
          <Box sx={{ mt: 0.5 }}>
            {app.proximasCitas.length > 0 ? (
              app.proximasCitas.map((cita, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{
                    color: '#0ea5e9',
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 38
                  }}>
                    {new Date(cita.fecha_cita).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <Typography sx={{
                    color: darkMode ? '#e2e8f0' : '#334155',
                    fontSize: 11,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 80, sm: 100 }
                  }}>
                    {cita.paciente_nombre?.split(' ')[0] || 'Paciente'}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: darkMode ? '#64748b' : '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>
                Sin citas próximas
              </Typography>
            )}
            {app.totalCitas > 2 && (
              <Typography sx={{ color: darkMode ? '#64748b' : '#94a3b8', fontSize: 10, mt: 0.25 }}>
                +{app.totalCitas - 2} más hoy
              </Typography>
            )}
          </Box>
        ) : app.metric !== undefined ? (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 22 },
                fontWeight: 800,
                background: app.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {app.metric}
            </Typography>
            <Typography sx={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: 10 }}>
              {app.metricLabel}
            </Typography>
          </Box>
        ) : app.metricIcon ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: darkMode ? '#94a3b8' : '#64748b' }}>
            {React.cloneElement(app.metricIcon, { sx: { fontSize: 14 } })}
            <Typography sx={{ fontSize: 10 }}>{app.metricLabel}</Typography>
          </Box>
        ) : null}
        {app.metricSub && !loadingMetrics && (
          <Typography sx={{ color: darkMode ? '#64748b' : '#94a3b8', fontSize: 10, display: { xs: 'none', sm: 'block' } }}>
            {app.metricSub}
          </Typography>
        )}
      </Box>

      {/* Arrow */}
      <ArrowForwardIcon
        sx={{
          color: darkMode ? '#475569' : '#cbd5e1',
          fontSize: 16,
          transition: 'transform 0.2s',
          display: { xs: 'none', sm: 'block' },
          '.MuiBox-root:hover &': { transform: 'translateX(4px)', color: app.shadowColor }
        }}
      />
    </Box>
  );

  // Componente de tarjeta compacta
  const CompactCard = ({ app }) => (
    <Box
      onClick={() => handleAppClick(app)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAppClick(app); }}
      role="button"
      tabIndex={app.available ? 0 : -1}
      aria-label={`${app.name}. ${app.description}${!app.available ? '. Próximamente' : ''}`}
      aria-disabled={!app.available}
      sx={getCardStyle(app, false)}
    >
      {/* Icon */}
      <Box
        sx={{
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          borderRadius: 2,
          background: app.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          boxShadow: `0 3px 10px ${app.shadowColor}`,
          '& svg': { fontSize: { xs: 18, sm: 20 } }
        }}
      >
        {app.icon}
      </Box>

      {/* Content */}
      <Box sx={{ textAlign: 'center', width: '100%' }}>
        <Typography
          sx={{
            color: darkMode ? '#f1f5f9' : '#1e293b',
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {app.name}
        </Typography>
        <Typography
          sx={{
            color: darkMode ? '#94a3b8' : '#64748b',
            fontSize: 10,
            display: { xs: 'none', sm: 'block' },
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {app.description}
        </Typography>
      </Box>

      {/* Badge */}
      {!app.available ? (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 8,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            background: darkMode ? 'rgba(71, 85, 105, 0.8)' : 'rgba(241, 245, 249, 0.9)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <Typography sx={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: 9, fontWeight: 700 }}>
            Pronto
          </Typography>
        </Box>
      ) : app.badge ? (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 8,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 8px rgba(236, 72, 153, 0.4)'
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>
            {app.badge}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  // Componente de sección
  const Section = ({ title, children }) => (
    <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          mb: 1,
          pl: 0.5
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(23, 66, 136, 0.98) 0%, rgba(15, 45, 95, 0.98) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 40%),
                           radial-gradient(circle at 40% 80%, rgba(245, 158, 11, 0.08) 0%, transparent 40%)`,
          pointerEvents: 'none'
        }}
      />

      {/* Main Content */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 2.5 },
          maxWidth: 1000,
          mx: 'auto',
          position: 'relative',
          zIndex: 5
        }}
      >
        {/* Welcome con fecha y resumen */}
        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
          {/* Saludo y nombre */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 500 }}>
              {getGreeting()},
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 }, fontWeight: 600 }}>
              {user?.displayName || 'Usuario'}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: 13, display: { xs: 'none', sm: 'block' } }}>
              •
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 13, display: { xs: 'none', sm: 'block' } }}>
              {getFormattedDate()}
            </Typography>
          </Box>
          {/* Mini resumen */}
          {!loadingMetrics && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 12 }}>
                Hoy:
              </Typography>
              <Typography sx={{ color: '#60a5fa', fontSize: 12, fontWeight: 600 }}>
                {metrics.citasHoy} citas
              </Typography>
              {metrics.citasPendientes > 0 && (
                <>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 12 }}>•</Typography>
                  <Typography sx={{ color: '#fbbf24', fontSize: 12, fontWeight: 600 }}>
                    {metrics.citasPendientes} pendientes
                  </Typography>
                </>
              )}
              {proximasCitas.length > 0 && (
                <>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 12 }}>•</Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 12 }}>
                    Sigue:
                  </Typography>
                  {proximasCitas.slice(0, 2).map((cita, idx) => {
                    const hora = new Date(cita.fecha_cita).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                    const nombre = cita.paciente_nombre?.split(' ')[0] || 'Paciente';
                    return (
                      <React.Fragment key={idx}>
                        {idx > 0 && <Typography sx={{ color: 'rgba(255, 255, 255, 0.2)', fontSize: 12 }}>,</Typography>}
                        <Typography sx={{ color: '#93c5fd', fontSize: 12, fontWeight: 600 }}>
                          {hora}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 12 }}>
                          {nombre}
                        </Typography>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Hero Cards - Acciones Principales */}
        <Section title="Acciones Principales">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)'
              },
              gap: { xs: 1, sm: 1.25 }
            }}
          >
            {heroApps.map((app) => (
              <HeroCard key={app.id} app={app} />
            ))}
          </Box>
        </Section>

        {/* Herramientas */}
        <Section title="Herramientas">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)'
              },
              gap: { xs: 1, sm: 1.25 }
            }}
          >
            {toolApps.map((app) => (
              <CompactCard key={app.id} app={app} />
            ))}
          </Box>
        </Section>

        {/* Administrativo - Solo Superadmins */}
        {isSuperAdmin && (
          <Section title="Administrativo">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)',
                  md: 'repeat(4, 1fr)'
                },
                gap: 1.5
              }}
            >
              {adminApps.map((app) => (
                <CompactCard key={app.id} app={app} />
              ))}
            </Box>
          </Section>
        )}
      </Box>

      {/* FAB - Quick Actions */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
        {/* FAB Actions */}
        {fabActions.map((action, index) => (
          <Zoom
            key={action.name}
            in={fabOpen}
            style={{ transitionDelay: fabOpen ? `${index * 50}ms` : '0ms' }}
          >
            <Tooltip title={action.name} placement="left" arrow>
              <Fab
                size="small"
                onClick={() => { action.action(); setFabOpen(false); }}
                sx={{
                  position: 'absolute',
                  bottom: 64 + (index * 52),
                  right: 4,
                  bgcolor: action.color,
                  color: '#fff',
                  boxShadow: `0 4px 14px ${action.color}66`,
                  '&:hover': { bgcolor: action.color, transform: 'scale(1.1)' }
                }}
              >
                {action.icon}
              </Fab>
            </Tooltip>
          </Zoom>
        ))}

        {/* Main FAB */}
        <Fab
          color="primary"
          onClick={() => setFabOpen(!fabOpen)}
          sx={{
            width: 56,
            height: 56,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
            transition: 'all 0.3s',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              transform: 'scale(1.05)'
            }
          }}
        >
          <AddIcon sx={{ transition: 'transform 0.3s', transform: fabOpen ? 'rotate(45deg)' : 'none' }} />
        </Fab>
      </Box>

      {/* Profile Dialog */}
      <Dialog open={openProfileDialog} onClose={() => setOpenProfileDialog(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}>
        <DialogTitle>Perfil de usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              src={firebasePhotoURL || user?.photoURL || ""}
              sx={{ width: 56, height: 56, bgcolor: "#6366f1", fontSize: 28 }}
              imgProps={{ referrerPolicy: "no-referrer" }}
            >
              {user?.displayName ? user.displayName[0] : "U"}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#334155" }}>{user?.displayName || "Usuario"}</Typography>
              <Typography sx={{ color: "#6366f1", fontSize: 15 }}>{user?.email}</Typography>
              <Typography sx={{ color: "#334155", fontSize: 15, mt: 0.5 }}><b>Empresa:</b> {subscriptionInfo?.empresa?.company || "No asignada"}</Typography>
            </Box>
          </Box>
          <Box sx={{ mb: 1, fontWeight: 500, color: "#334155" }}><b>Plan actual:</b> {userPlan || "Cargando..."}</Box>

          {/* Horario de atención */}
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0", color: "#334155" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, fontSize: 15, color: "#1e3a5f" }}>
                <ScheduleIcon sx={{ fontSize: 20, color: "#6366f1" }} />
                Horario de Atención
              </Box>
              {!editingAgenda && userAgenda && (
                <IconButton size="small" onClick={startEditingAgenda} sx={{ color: "#6366f1", "&:hover": { background: "#e0e7ff" } }}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {editingAgenda ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1 }}>Días de atención</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {DIAS_SEMANA.map(dia => (
                      <Chip
                        key={dia.id}
                        label={dia.label}
                        size="small"
                        onClick={() => toggleDia(dia.id)}
                        sx={{
                          fontWeight: 600, fontSize: 12, cursor: "pointer", transition: "all 0.2s",
                          ...(editAgendaDias.includes(dia.id) ? {
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#fff"
                          } : {
                            background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0"
                          })
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14 }} /> Hora inicio
                    </Typography>
                    <TextField type="time" size="small" fullWidth value={editAgendaHoraInicio} onChange={(e) => setEditAgendaHoraInicio(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14 }} /> Hora fin
                    </Typography>
                    <TextField type="time" size="small" fullWidth value={editAgendaHoraFin} onChange={(e) => setEditAgendaHoraFin(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }} />
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TimerIcon sx={{ fontSize: 14 }} /> Duración de citas
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {INTERVALOS.map(int => (
                      <Chip
                        key={int.value}
                        label={int.label}
                        size="small"
                        onClick={() => setEditAgendaIntervalo(int.value)}
                        sx={{
                          fontWeight: 500, fontSize: 12, cursor: "pointer", transition: "all 0.2s",
                          ...(editAgendaIntervalo === int.value ? {
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff"
                          } : {
                            background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0"
                          })
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={cancelEditingAgenda} disabled={savingAgenda} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>Cancelar</Button>
                  <Button size="small" variant="contained" onClick={saveAgenda} disabled={savingAgenda} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" }}>{savingAgenda ? "Guardando..." : "Guardar"}</Button>
                </Box>
              </Box>
            ) : userAgenda ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2, background: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>Días</Typography>
                    <Typography sx={{ fontWeight: 600, color: "#334155", fontSize: 14 }}>{userAgenda.dias || "No configurado"}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>Horario</Typography>
                    <Typography sx={{ fontWeight: 600, color: "#334155", fontSize: 14 }}>{userAgenda.hora_inicio || "—"} - {userAgenda.hora_fin || "—"}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TimerIcon sx={{ fontSize: 16, color: "#d97706" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>Duración citas</Typography>
                    <Typography sx={{ fontWeight: 600, color: "#334155", fontSize: 14 }}>{userAgenda.intervalo ? `${userAgenda.intervalo} minutos` : "No configurado"}</Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: "center", background: "#f8fafc", borderRadius: 2, border: "1px dashed #cbd5e1" }}>
                <ScheduleIcon sx={{ fontSize: 36, color: "#94a3b8", mb: 1 }} />
                <Typography sx={{ fontSize: 14, color: "#64748b", mb: 1.5 }}>No hay horario configurado</Typography>
                <Button size="small" variant="contained" onClick={startEditingAgenda} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" }}>Configurar horario</Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <CalculadorasMedicas open={openCalculadoras} onClose={() => setOpenCalculadoras(false)} />
      <CentroAyuda open={openAyuda} onClose={() => setOpenAyuda(false)} />

      {/* WhatsApp Dialog */}
      <Dialog
        open={openWhatsAppDialog}
        onClose={() => setOpenWhatsAppDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography sx={{ fontWeight: 600 }}>Enviar Mensaje de WhatsApp</Typography>
        </DialogTitle>
        <DialogContent>
          {waError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setWaError(null)}>
              {waError}
            </Alert>
          )}
          {waSuccess && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setWaSuccess(null)}>
              {waSuccess}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Número de destino"
            placeholder="+521234567890"
            value={waNumeroDestino}
            onChange={(e) => setWaNumeroDestino(e.target.value)}
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#128C7E' }} /></InputAdornment>
            }}
            helperText="Incluye el código de país (ej: +52 para México)"
          />

          <TextField
            fullWidth
            label="Mensaje"
            multiline
            rows={4}
            value={waMensaje}
            onChange={(e) => setWaMensaje(e.target.value)}
            placeholder="Escribe tu mensaje aquí..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenWhatsAppDialog(false)} color="inherit" sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEnviarWhatsApp}
            disabled={waEnviando || !waNumeroDestino || !waMensaje}
            startIcon={waEnviando ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ bgcolor: '#25D366', borderRadius: 2, '&:hover': { bgcolor: '#1ebe5d' } }}
          >
            {waEnviando ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
